/**
 * @fileoverview SimHash implementation for local-first fingerprinting.
 *
 * WHAT THIS FILE DOES
 * - Implements a 64-bit SimHash algorithm using BigInt.
 * - Provides tokenization utilities (word n-grams) that work across
 *   Node.js, Cloudflare Workers, and browser content scripts.
 * - Exposes helper functions for availability fingerprints (single SimHash contract).
 * - Also exposes legacy helpers for identity + version fingerprints (deprecated).
 *
 * WHY THIS FILE EXISTS
 * - The local-first index requires deterministic fingerprints on-device.
 * - SimHash provides near-duplicate matching with small Hamming thresholds.
 * - We need one canonical implementation shared by backend and extension.
 *
 * ARCHITECTURE ROLE
 * - Part of @gist-protocol/core shared utilities.
 * - Synced into the Chrome extension via scripts/sync-protocol-shared.js.
 * - Imported by backend fingerprinting and extension matching logic.
 *
 * ALGORITHM OVERVIEW
 * 1. Tokenize normalized text into word n-grams.
 * 2. Hash each token to 64 bits (FNV-1a).
 * 3. Accumulate weighted bit vectors (+weight or -weight).
 * 4. Emit 1 for positions with positive sums (0 otherwise).
 *
 * FINGERPRINT TYPES
 * - Availability: first 10,000 chars of normalized body (single SimHash) [CURRENT CONTRACT].
 *   Use computeAvailabilityFingerprint() for all new code.
 * - Identity: title + author + first 1,000 chars of normalized body (legacy, deprecated).
 * - Version: first 10,000 chars of normalized body (legacy, deprecated).
 *   Backend still accepts versionSimHash for backward compatibility with old clients.
 * - Completeness: normalized body length (character count).
 *
 * CALLED BY
 * - packages/backend (future simhash storage and index generation)
 * - packages/chrome-plugin (local matching in web workers)
 * - packages/protocol/shared/content-normalization.js (normalization contract)
 *
 * THIS MODULE CALLS
 * - normalizeContentText() for deterministic preprocessing
 * - BigInt arithmetic for 64-bit operations
 * - RegExp tokenization with Unicode property escapes
 *
 * EDGE CASES
 * - Empty body => computeContentFingerprints() returns null; computeSimHash() returns 0n.
 * - Scripts without whitespace => fallback to character n-grams.
 * - Short input (< n-gram size) => falls back to unigrams.
 * - Non-Latin scripts => tokenized via Unicode \p{L} and \p{N} matches.
 *
 * PERFORMANCE
 * - Target: <10ms for 10k characters in modern V8.
 * - BigInt operations are used only during fingerprint generation (not index scan).
 *
 * DATA FLOW SUMMARY
 * - raw text -> normalizeContentText() -> tokenizeSimHashText() -> computeSimHash().
 * - computeAvailabilityFingerprint() wraps the availability slice.
 * - computeContentFingerprints() wraps identity + version slices (legacy).
 *
 * RUNTIME NOTES
 * - Requires BigInt (available in modern Chrome, Node 18+, Workers).
 * - Requires Unicode property escapes (\\p{L}, \\p{N}) in RegExp.
 *
 * CHANGE SAFETY
 * - Changing tokenization or hash constants invalidates the entire index.
 * - Any changes must be coordinated with backend + extension rollouts.
 *
 * NON-GOALS
 * - Does not implement index search or Hamming threshold filtering.
 * - Does not store or transmit fingerprints (pure computation only).
 *
 * EXAMPLE
 * - computeSimHashHex(normalizeContentText('hello world')) -> \"...\"
 *
 * TESTING
 * - packages/protocol/shared/__tests__/simhash.test.js
 * - packages/chrome-plugin/extension/shared/simhash.test.js
 *
 * RELATED FILES
 * - packages/protocol/shared/content-normalization.js
 * - README/Plans/features/auto-gist/086-local-first-simhash-index-plan.md
 * - packages/chrome-plugin/scripts/sync-protocol-shared.js
 */

import { normalizeContentText } from './content-normalization.js';

/** Total number of bits in the SimHash fingerprint. */
export const SIMHASH_BITS = 64;

/** Hex string length for a 64-bit fingerprint (16 hex chars). */
export const SIMHASH_HEX_LENGTH = 16;

/**
 * Character limit for the identity fingerprint body segment.
 *
 * Why 1,000 chars:
 * - Captures title + opening paragraphs reliably.
 * - Avoids noise from long-tail content differences.
 */
export const IDENTITY_BODY_MAX_CHARS = 1000;

/**
 * Character limit for the version fingerprint body segment.
 *
 * Why 10,000 chars:
 * - Captures most articles without hashing massive payloads.
 * - Provides room for meaningful edits while staying fast.
 */
export const VERSION_BODY_MAX_CHARS = 10000;

/** Default word n-gram size for tokenization. */
const DEFAULT_NGRAM_SIZE = 3;

/** Minimum token length to keep (1 keeps CJK single-character tokens). */
const DEFAULT_MIN_TOKEN_CHARS = 1;

/**
 * Safety cap for token counts to avoid pathological inputs.
 *
 * 20k tokens ~= hundreds of KB of text, far beyond our 10k char target.
 */
const DEFAULT_MAX_TOKENS = 20000;

/**
 * Character n-gram size used when text lacks whitespace (CJK, Thai, etc.).
 *
 * We use 3-grams because they offer enough context to reduce collisions
 * while still being robust to small edits.
 */
const CHAR_NGRAM_SIZE = 3;

/**
 * Minimum length before enabling the character n-gram fallback.
 *
 * Why 16 chars:
 * - Produces at least 14 three-grams, which is enough for stable matching.
 * - Avoids overfitting tiny strings (e.g., short titles).
 */
const CHAR_NGRAM_FALLBACK_MIN_LENGTH = 16;

/**
 * Maximum word count to allow before skipping character n-gram fallback.
 *
 * We only use character n-grams when tokenization yields a single word,
 * which typically happens for scripts without whitespace.
 */
const CHAR_NGRAM_FALLBACK_MAX_WORDS = 1;

/** Removes whitespace before character n-gram generation. */
const CHAR_NGRAM_WHITESPACE_PATTERN = /\s+/g;

/** FNV-1a 64-bit offset basis (per FNV-1a 64-bit spec). */
const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;

/** FNV-1a 64-bit prime (per FNV-1a 64-bit spec). */
const FNV_PRIME = 0x100000001b3n;

/** Mask to constrain BigInt values to 64 bits. */
const SIMHASH_MASK = (1n << 64n) - 1n;

/** Word token pattern using Unicode property escapes. */
const WORD_PATTERN = /[\p{L}\p{N}]+(?:['\u2019_-][\p{L}\p{N}]+)*/gu;

/** Separator used when joining n-gram tokens. */
const NGRAM_JOINER = ' ';

/**
 * @typedef {Object} SimHashTokenizationOptions
 * @property {number} [ngramSize] - Word n-gram size (defaults to 3).
 * @property {number} [minTokenLength] - Minimum token length to keep.
 * @property {number} [maxTokens] - Hard cap on token count.
 */

/**
 * @typedef {Object} ContentFingerprintInput
 * @property {string | null | undefined} [title] - Article title or null.
 * @property {string | null | undefined} [author] - Author/byline or null.
 * @property {string | null | undefined} [body] - Raw text body.
 * @property {SimHashTokenizationOptions} [tokenization] - Tokenization overrides.
 */

/**
 * @typedef {Object} ContentFingerprints
 * @property {string} identitySimHash - 64-bit identity SimHash hex string (legacy).
 * @property {string} versionSimHash - 64-bit version SimHash hex string (legacy).
 * @property {number} completenessScore - Normalized body length (chars).
 */

/**
 * @typedef {Object} AvailabilityFingerprintInput
 * @property {string | null | undefined} [body] - Raw text body.
 * @property {SimHashTokenizationOptions} [tokenization] - Tokenization overrides.
 */

/**
 * Normalizes tokenization options with safe defaults.
 *
 * @param {SimHashTokenizationOptions | undefined} options
 * @returns {{ ngramSize: number, minTokenLength: number, maxTokens: number }}
 */
function normalizeTokenizationOptions(options) {
  const ngramSize = Number.isInteger(options?.ngramSize) && options.ngramSize > 0
    ? options.ngramSize
    : DEFAULT_NGRAM_SIZE;
  const minTokenLength = Number.isInteger(options?.minTokenLength) && options.minTokenLength > 0
    ? options.minTokenLength
    : DEFAULT_MIN_TOKEN_CHARS;
  const maxTokens = Number.isInteger(options?.maxTokens) && options.maxTokens > 0
    ? options.maxTokens
    : DEFAULT_MAX_TOKENS;

  return { ngramSize, minTokenLength, maxTokens };
}

/**
 * Tokenizes normalized text into word n-grams for SimHash.
 *
 * @param {string} text - Normalized text input.
 * @param {SimHashTokenizationOptions} [options]
 * @returns {string[]} Array of n-gram tokens.
 */
export function tokenizeSimHashText(text, options) {
  if (typeof text !== 'string' || text.length === 0) {
    return [];
  }

  const { ngramSize, minTokenLength, maxTokens } = normalizeTokenizationOptions(options);
  const words = text.match(WORD_PATTERN) ?? [];
  const filteredWords = minTokenLength > 1
    ? words.filter((token) => token.length >= minTokenLength)
    : words;
  const wordCount = filteredWords.length;

  if (wordCount === 0) {
    return [];
  }

  let tokens = ngramSize <= 1 || wordCount < ngramSize
    ? filteredWords
    : buildWordNgrams(filteredWords, ngramSize);

  if (shouldUseCharNgrams(text, wordCount)) {
    tokens = buildCharacterNgrams(text, CHAR_NGRAM_SIZE);
  }

  if (tokens.length > maxTokens) {
    return tokens.slice(0, maxTokens);
  }

  return tokens;
}

/**
 * Builds word n-grams from a word list.
 *
 * @param {string[]} words
 * @param {number} ngramSize
 * @returns {string[]}
 */
function buildWordNgrams(words, ngramSize) {
  const total = words.length - ngramSize + 1;
  const ngrams = new Array(total);

  for (let i = 0; i < total; i += 1) {
    ngrams[i] = words.slice(i, i + ngramSize).join(NGRAM_JOINER);
  }

  return ngrams;
}

/**
 * Determines whether to fall back to character n-grams.
 *
 * We only fall back when word tokenization produces a single word
 * and the text is long enough to warrant char-level comparison.
 *
 * @param {string} text
 * @param {number} wordCount
 * @returns {boolean}
 */
function shouldUseCharNgrams(text, wordCount) {
  if (wordCount > CHAR_NGRAM_FALLBACK_MAX_WORDS) {
    return false;
  }

  return text.length >= CHAR_NGRAM_FALLBACK_MIN_LENGTH;
}

/**
 * Builds character n-grams from normalized text.
 *
 * @param {string} text
 * @param {number} ngramSize
 * @returns {string[]}
 */
function buildCharacterNgrams(text, ngramSize) {
  const compact = text.replace(CHAR_NGRAM_WHITESPACE_PATTERN, '');
  if (!compact) {
    return [];
  }

  const chars = Array.from(compact);
  if (chars.length < ngramSize) {
    return [compact];
  }

  const total = chars.length - ngramSize + 1;
  const ngrams = new Array(total);

  for (let i = 0; i < total; i += 1) {
    ngrams[i] = chars.slice(i, i + ngramSize).join('');
  }

  return ngrams;
}

/**
 * Counts token occurrences for weighting.
 *
 * @param {string[]} tokens
 * @returns {Map<string, number>}
 */
function countTokens(tokens) {
  const counts = new Map();
  for (const token of tokens) {
    const current = counts.get(token) ?? 0;
    counts.set(token, current + 1);
  }
  return counts;
}

/**
 * Hashes a token to a 64-bit BigInt using FNV-1a.
 *
 * @param {string} token
 * @returns {bigint}
 */
function hashTokenTo64Bit(token) {
  let hash = FNV_OFFSET_BASIS;

  for (const char of token) {
    // String iteration yields Unicode code points (surrogate pairs stay intact).
    const codePoint = BigInt(char.codePointAt(0) ?? 0);
    hash ^= codePoint;
    hash = (hash * FNV_PRIME) & SIMHASH_MASK;
  }

  return hash;
}

/**
 * Computes a 64-bit SimHash fingerprint for normalized text.
 *
 * @param {string} text - Normalized text input (already lowercased).
 * @param {SimHashTokenizationOptions} [options]
 * @returns {bigint} 64-bit SimHash value.
 */
export function computeSimHash(text, options) {
  const tokens = tokenizeSimHashText(text, options);
  if (tokens.length === 0) {
    // Empty input maps to an all-zero fingerprint.
    return 0n;
  }

  const counts = countTokens(tokens);
  const accumulator = new Int32Array(SIMHASH_BITS);

  for (const [token, weight] of counts) {
    const hashed = hashTokenTo64Bit(token);
    for (let bit = 0; bit < SIMHASH_BITS; bit += 1) {
      const mask = 1n << BigInt(bit);
      accumulator[bit] += (hashed & mask) !== 0n ? weight : -weight;
    }
  }

  let fingerprint = 0n;
  for (let bit = 0; bit < SIMHASH_BITS; bit += 1) {
    if (accumulator[bit] >= 0) {
      fingerprint |= 1n << BigInt(bit);
    }
  }

  return fingerprint & SIMHASH_MASK;
}

/**
 * Converts a 64-bit SimHash BigInt into a zero-padded hex string.
 *
 * @param {bigint} simhash
 * @returns {string} 16-character hex string (lowercase).
 */
export function simHashToHex(simhash) {
  const normalized = simhash & SIMHASH_MASK;
  return normalized.toString(16).padStart(SIMHASH_HEX_LENGTH, '0');
}

/**
 * Parses a hex string into a 64-bit SimHash BigInt.
 *
 * @param {string} hex
 * @returns {bigint | null} Parsed BigInt or null for invalid input.
 */
export function parseSimHashHex(hex) {
  if (typeof hex !== 'string') {
    return null;
  }

  const trimmed = hex.trim();
  if (!/^[0-9a-fA-F]{1,16}$/.test(trimmed)) {
    return null;
  }

  return BigInt(`0x${trimmed}`) & SIMHASH_MASK;
}

/**
 * Computes a SimHash hex string directly from normalized text.
 *
 * @param {string} text - Normalized text input.
 * @param {SimHashTokenizationOptions} [options]
 * @returns {string} 16-character hex string.
 */
export function computeSimHashHex(text, options) {
  return simHashToHex(computeSimHash(text, options));
}

/**
 * Computes Hamming distance between two 64-bit SimHash values.
 *
 * @param {bigint} left
 * @param {bigint} right
 * @throws {TypeError} When inputs are not BigInt.
 * @returns {number} Number of differing bits (0-64).
 */
export function hammingDistance(left, right) {
  if (typeof left !== 'bigint' || typeof right !== 'bigint') {
    throw new TypeError('hammingDistance expects BigInt inputs');
  }

  let xor = (left ^ right) & SIMHASH_MASK;
  let distance = 0;

  while (xor !== 0n) {
    // Kernighan's bit count: clear the lowest set bit each iteration.
    xor &= xor - 1n;
    distance += 1;
  }

  return distance;
}

/**
 * Computes the availability fingerprint (single SimHash) from raw content.
 *
 * Availability fingerprints use the normalized body only (first 10k chars).
 * Title/author are intentionally excluded to avoid false negatives when
 * metadata shifts or template chrome changes.
 *
 * Called by:
 * - (Phase 1+) local index matching in the extension
 * - (Phase 3+) /api/availability backend endpoint
 *
 * This function calls:
 * - normalizeContentText() for deterministic preprocessing
 * - computeSimHashHex() on the normalized body slice
 *
 * @param {AvailabilityFingerprintInput} input
 * @returns {string | null} 64-bit SimHash hex string, or null if body is empty.
 */
export function computeAvailabilityFingerprint(input) {
  const normalizedBody = normalizeContentText(input?.body ?? '');
  if (!normalizedBody) {
    return null;
  }

  const availabilitySeed = normalizedBody.slice(0, VERSION_BODY_MAX_CHARS);
  return computeSimHashHex(availabilitySeed, input?.tokenization);
}

/**
 * Computes identity + version fingerprints and completeness from raw content.
 *
 * @param {ContentFingerprintInput} input
 * @returns {ContentFingerprints | null} Fingerprints, or null if body is empty.
 */
export function computeContentFingerprints(input) {
  const normalizedBody = normalizeContentText(input?.body ?? '');
  if (!normalizedBody) {
    return null;
  }

  const normalizedTitle = normalizeContentText(input?.title ?? '');
  const normalizedAuthor = normalizeContentText(input?.author ?? '');
  const bodySnippet = normalizedBody.slice(0, IDENTITY_BODY_MAX_CHARS);

  const identityParts = [normalizedTitle, normalizedAuthor, bodySnippet].filter(Boolean);
  const identitySeed = identityParts.join(NGRAM_JOINER);
  const versionSeed = normalizedBody.slice(0, VERSION_BODY_MAX_CHARS);

  const tokenization = input?.tokenization;

  return {
    identitySimHash: computeSimHashHex(identitySeed, tokenization),
    versionSimHash: computeSimHashHex(versionSeed, tokenization),
    completenessScore: normalizedBody.length
  };
}
